import type { DuprProfileDto, DuprRatingDto } from "@volley/data";

/** A DUPR rating's value only if it's a usable number ("NR"/null → not usable). */
export function usableRatingValue(
    rating: DuprRatingDto | undefined,
): string | null {
    const value = rating?.value;
    if (value == null) return null;
    return Number.isNaN(Number.parseFloat(value)) ? null : value;
}

/**
 * The DUPR value to capture on the setup screen: prefer doubles, fall back to
 * singles. Returns null when neither format has a usable number (e.g. both
 * "NR"), in which case the user must enter a rating manually. This is a display/
 * seed helper and intentionally does not distinguish official vs AI-annotated —
 * use {@link hasOfficialDuprRating} for the assessment-gating decision.
 */
export function pickDuprRating(profile: DuprProfileDto): string | null {
    return (
        usableRatingValue(profile.doubles) ?? usableRatingValue(profile.singles)
    );
}

/** True when a format holds a usable OFFICIAL number (not null/"NR"/AI-annotated). */
function isOfficialRating(rating: DuprRatingDto): boolean {
    if (rating.aiAnnotated) return false;
    return usableRatingValue(rating) != null;
}

/**
 * Whether a linked profile carries an official DUPR **doubles** rating.
 * Gates the self-serve assessment (only for players without one). Only doubles
 * counts: Volley only ever submits a doubles rating, so a player who has a
 * singles rating but no doubles can still take the assessment to establish their
 * doubles rating. Mirrors the server-side check in `routes/journeys/journeys.ts`;
 * Volley's own AI-annotated (predicted) ratings do not count as official.
 *
 * "Only ever submits doubles" is enforced by `pushProvisionalRating`
 * (webby-server integrations/dupr/partner-api.service.ts), which defaults to the
 * doubles field. That is the site to check if this assumption ever looks wrong —
 * it once sent singles, which routed submissions to a field nothing here reads.
 */
export function hasOfficialDuprRating(profile: DuprProfileDto): boolean {
    return isOfficialRating(profile.doubles);
}
